import { ErrorHandler, inject, Injectable } from "@angular/core";
import { API_REQUEST_METHODS, HttpService } from "../../../ancestors/base-api.service";
import { GoalSettingScorecardTypeEnum, MonitoringGoalSettingCardStatusEnum, MonitoringGoalSettingProposalStatusEnum, MonitoringGoalSettingSummary, MonitoringGoalSettingUpdateFilterEnum, MonitoringGoalSettingUserFiltersValues, MonitoringGoalSettingUserSortEnum, MonitoringGoalSettingUserSummary, MonitoringGoalSettingUserSummaryIds } from "alloy-performance-engine-cl";
import { ApeDownloadButtonService } from "../../components/ape-download-button/ape-download-button.service";
import { DownloadUtil } from "../../util/download.util";

/**
 * Filtri/Query string parameters condivisi tra le chiamate del monitoraggio Goal Setting.
 */
export interface MonitoringGoalSettingQueryParams {
  searchedText?: string;
  cardStatus?: MonitoringGoalSettingCardStatusEnum[];
  proposalStatus?: MonitoringGoalSettingProposalStatusEnum[];
  updates?: MonitoringGoalSettingUpdateFilterEnum[];
  scorecardType?: GoalSettingScorecardTypeEnum[];
  department?: string[];
  evaluators?: string[];
}

export interface MonitoringGoalSettingListQueryParams extends MonitoringGoalSettingQueryParams {
  sortBy?: MonitoringGoalSettingUserSortEnum;
  fromRecord?: number;
  numRecords?: number;
}

/**
 * API client per il monitoraggio Goal Setting (/backoffice/monitoring/goal-setting/*).
 * Mirror di MonitoringBackofficeApiService ma scoped sui processi GOAL_SETTING.
 */
@Injectable({ providedIn: "root" })
export class MonitoringGoalSettingBackofficeApiService extends HttpService {
  private errorHandler = inject(ErrorHandler);

  private readonly downloadService = inject(ApeDownloadButtonService);

  constructor() {
    super();
    this.logger.setCaller("MonitoringGoalSettingBackofficeApiService");
    this.createInstance({ url: this.env.apiBaseUrl });
  }

  /**
   * Recupera i KPI di processo Goal Setting (filtrati dinamicamente).
   */
  async getProcessSummary(processId: string, filters?: MonitoringGoalSettingQueryParams): Promise<MonitoringGoalSettingSummary> {
    try {
      if (!processId?.trim()) {
        throw new Error("Process ID is required");
      }
      const response = await this.httpClient<MonitoringGoalSettingSummary>(`/backoffice/monitoring/goal-setting/process/summary/${processId}`, {
        method: API_REQUEST_METHODS.GET,
        query: filters || {}
      });
      if (!response) {
        throw new Error("Invalid GS process summary response");
      }
      return response;
    } catch (error: unknown) {
      this.errorHandler.handleError(error);
      throw error;
    }
  }

  /**
   * Recupera i valori dei filtri disponibili (manager, department, enum di stato).
   */
  async getUserFiltersValues(processId: string): Promise<MonitoringGoalSettingUserFiltersValues> {
    try {
      if (!processId?.trim()) {
        throw new Error("Process ID is required");
      }
      const response = await this.httpClient<MonitoringGoalSettingUserFiltersValues>(`/backoffice/monitoring/goal-setting/user/filters/values/${processId}`, { method: API_REQUEST_METHODS.GET });
      if (!response) {
        throw new Error("Invalid GS user filters values response");
      }
      return response;
    } catch (error: unknown) {
      this.errorHandler.handleError(error);
      throw error;
    }
  }

  /**
   * Recupera la lista paginata + ordinata + filtrata degli utenti del processo Goal Setting.
   */
  async getUsersData(processId: string, params?: MonitoringGoalSettingListQueryParams): Promise<MonitoringGoalSettingUserSummary[]> {
    try {
      if (!processId?.trim()) {
        throw new Error("Process ID is required");
      }
      const response = await this.httpClient<MonitoringGoalSettingUserSummary[]>(`/backoffice/monitoring/goal-setting/user/list/${processId}`, {
        method: API_REQUEST_METHODS.GET,
        query: params || {}
      });
      if (!response) {
        throw new Error("Invalid GS users list response");
      }
      return response;
    } catch (error: unknown) {
      this.errorHandler.handleError(error);
      throw error;
    }
  }

  /**
   * Conta gli utenti del processo Goal Setting che matchano i filtri attivi.
   */
  async getUsersDataCount(processId: string, filters?: MonitoringGoalSettingQueryParams): Promise<number> {
    try {
      if (!processId?.trim()) {
        throw new Error("Process ID is required");
      }
      const response = await this.httpClient<number>(`/backoffice/monitoring/goal-setting/user/count/${processId}`, {
        method: API_REQUEST_METHODS.GET,
        query: filters || {}
      });
      if (response === undefined || response === null) {
        throw new Error("Invalid GS users count response");
      }
      return response || 0;
    } catch (error: unknown) {
      this.errorHandler.handleError(error);
      throw error;
    }
  }

  /**
   * Esporta in Excel l'insieme completo dei dati del processo Goal Setting per l'amministratore.
   * Il file ha 3 fogli (Anagrafica/avanzamento, Dettaglio obiettivi, Dettaglio proposte) e include
   * tutti gli utenti del processo: i filtri di lista NON influenzano il contenuto del file.
   * @param processId - ID del processo Goal Setting da esportare
   * @param downloadId - ID univoco del download per gestire lo stato del pulsante ape-download-button
   */
  async exportGoalSettingMonitoring(processId: string, downloadId: string): Promise<void> {
    try {
      if (!processId?.trim()) {
        throw new Error("Process ID is required");
      }

      this.logger.info(`Exporting GS monitoring report for process ${processId}`);

      const response = await this.httpClient<{
        fileName: string;
        fileUrl: string;
      }>(`/backoffice/monitoring/goal-setting/process/export/${processId}`, {
        method: API_REQUEST_METHODS.GET,
        isFileDownload: true
      });

      if (!this.downloadService.isDownloadValid(downloadId)) {
        this.logger.info(`Download ${downloadId} was cancelled during API call, skipping file download`);
        return;
      }

      if (!response.fileName || !response.fileUrl) {
        throw new Error("Invalid GS monitoring export response");
      }

      const fallbackFilename = `goal-setting-${processId}.xlsx`;
      DownloadUtil.downloadFromHttpServiceResponse(response, fallbackFilename);

      this.logger.info("GS monitoring report exported successfully");
    } catch (error: unknown) {
      this.logger.error("Error exporting GS monitoring report:", String(error));
      this.errorHandler.handleError(error);
      throw error;
    } finally {
      this.downloadService.completeDownload(downloadId);
    }
  }

  /**
   * Scarica il PDF della scheda obiettivi di un utente nel processo Goal Setting.
   * Disponibile per SUPERADMIN (sempre) e ADMIN (solo per processi del proprio country).
   * @param processId - ID del processo Goal Setting
   * @param userId - ID dell'utente di cui scaricare la scheda
   * @param downloadId - ID univoco del download per gestire lo stato del pulsante ape-download-button
   */
  async downloadGoalSettingUserScorecardPdf(processId: string, userId: string, downloadId: string): Promise<void> {
    try {
      if (!processId?.trim()) {
        throw new Error("Process ID is required");
      }
      if (!userId?.trim()) {
        throw new Error("User ID is required");
      }

      this.logger.info(`Downloading GS scorecard PDF for user ${userId} in process ${processId}`);

      const response = await this.httpClient<{
        fileName: string;
        fileUrl: string;
      }>(`/backoffice/monitoring/goal-setting/process/${processId}/user/${userId}/scorecard/pdf`, {
        method: API_REQUEST_METHODS.GET,
        isFileDownload: true
      });

      if (!this.downloadService.isDownloadValid(downloadId)) {
        this.logger.info(`Download ${downloadId} was cancelled during API call, skipping file download`);
        return;
      }

      if (!response.fileName || !response.fileUrl) {
        throw new Error("Invalid GS scorecard PDF response");
      }

      const fallbackFilename = `obiettivi-${userId}-${processId}.pdf`;
      DownloadUtil.downloadFromHttpServiceResponse(response, fallbackFilename);

      this.logger.info("GS scorecard PDF downloaded successfully");
    } catch (error: unknown) {
      this.logger.error("Error downloading GS scorecard PDF:", String(error));
      this.errorHandler.handleError(error);
      throw error;
    } finally {
      this.downloadService.completeDownload(downloadId);
    }
  }

  /**
   * Recupera tutti gli identificativi (processUserId, userId) degli utenti che matchano i filtri attivi.
   */
  async getAllUsersDataForChecked(processId: string, filters?: MonitoringGoalSettingQueryParams): Promise<MonitoringGoalSettingUserSummaryIds[]> {
    try {
      if (!processId?.trim()) {
        throw new Error("Process ID is required");
      }
      const response = await this.httpClient<MonitoringGoalSettingUserSummaryIds[]>(`/backoffice/monitoring/goal-setting/user/id/list/all/${processId}`, {
        method: API_REQUEST_METHODS.GET,
        query: filters || {}
      });
      if (!response) {
        throw new Error("Invalid GS user ids response");
      }
      return response;
    } catch (error: unknown) {
      this.errorHandler.handleError(error);
      throw error;
    }
  }
}
